import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import LoadingSpinner from "../common/loading";
import SystemMessage from "./system-message";
import ChatBlock from "./chat-block";
import ChatLanding from "./chat-landing";

const ChatSession = (
  { sessionData, loading, errorMessage, onAssistantInteraction },
  ref
) => {
  const messagesContainerRef = useRef(null);
  const [messageBlocks, setMessageBlocks] = useState(new Map());
  const [blockOrder, setBlockOrder] = useState([]);
  const [stoppedBlocks, setStoppedBlocks] = useState(new Set());
  const blockRefs = useRef(new Map());

  // Remove the standalone showLanding logic since we handle landing blocks internally

  // Add new block function
  const addNewBlock = useCallback((queryId, block) => {
    setMessageBlocks((prevBlocks) => {
      const newBlocks = new Map(prevBlocks);
      newBlocks.set(queryId, block);
      return newBlocks;
    });

    setBlockOrder((prevOrder) => {
      if (!prevOrder.includes(queryId)) {
        const newOrder = [...prevOrder, queryId];
        return newOrder;
      }
      return prevOrder;
    });
  }, []);

  // Add message to existing block
  const addMessageToBlock = useCallback((queryId, message) => {
    setMessageBlocks((prevBlocks) => {
      const newBlocks = new Map(prevBlocks);

      if (newBlocks.has(queryId)) {
        // Update existing block - only this block will re-render
        const existingBlock = newBlocks.get(queryId) || [];
        const updatedBlock = {
          ...existingBlock,
          messages: [...existingBlock.messages, message],
        };
        newBlocks.set(queryId, updatedBlock);
      }

      return newBlocks;
    });
  }, []);

  // Mark block as complete
  const markBlockComplete = useCallback((queryId) => {
    const blockRef = blockRefs.current.get(queryId);
    if (blockRef && blockRef.markComplete) {
      blockRef.markComplete();
    }
  }, []);

  // Mark block as stopped
  const markBlockStopped = useCallback((queryId) => {
    setStoppedBlocks((prevStopped) => new Set([...prevStopped, queryId]));
    const blockRef = blockRefs.current.get(queryId);
    if (blockRef && blockRef.markStopped) {
      blockRef.markStopped();
    }
  }, []);

  // Render individual blocks - each ChatBlock is memoized and will only re-render when its specific block changes
  const renderedBlocks = useMemo(() => {
    return blockOrder.map((queryId) => {
      const block = messageBlocks.get(queryId);
      if (!block) return null;

      // Handle landing block type
      if (block.from === "landing") {
        return <ChatLanding key={queryId} />;
      }

      const isBlockStopped = stoppedBlocks.has(queryId);

      return (
        <ChatBlock
          key={queryId}
          ref={(ref) => {
            if (ref) {
              blockRefs.current.set(queryId, ref);
            } else {
              blockRefs.current.delete(queryId);
            }
          }}
          block={block}
          onAssistantInteraction={onAssistantInteraction}
          isStopped={isBlockStopped}
        />
      );
    });
  }, [blockOrder, messageBlocks, stoppedBlocks, onAssistantInteraction]);

  // Initialize session with provided data
  useEffect(() => {
    if (!sessionData) return;

    // Clear any existing blocks first
    setMessageBlocks(new Map());
    setBlockOrder([]);
    setStoppedBlocks(new Set());
    blockRefs.current.clear();

    if (sessionData.isLandingOnly) {
      // Add landing block for landing-only sessions
      const landingQueryId = `landing`;
      const landingBlock = {
        from: "landing",
        queryId: landingQueryId,
        messages: [],
      };
      addNewBlock(landingQueryId, landingBlock);
    } else {
      // Handle sessions with initData
      const { initData } = sessionData;
      // Combine all messages and reqMessages, then group by queryId while preserving order
      const allMessages = [];

      // Add reqMessages first
      if (initData.reqMessages && Array.isArray(initData.reqMessages)) {
        initData.reqMessages.forEach((reqMessage) => {
          allMessages.push({
            queryId: reqMessage.queryId,
            type: "REQ",
            text: reqMessage.text,
            audioFile: reqMessage.audioFile,
            mentions: reqMessage.mentions,
            timestamp: reqMessage.timestamp,
            step: 0,
            parentStep: -1,
          });
        });
      }

      // Add serviceMessages
      if (initData.serviceMessages && Array.isArray(initData.serviceMessages)) {
        initData.serviceMessages.forEach((serviceMessage) => {
          // Check if this is a notification or dialog message stored as a service message
          if (serviceMessage.type) {
            allMessages.push({
              ...serviceMessage,
            });
          } else {
            allMessages.push({
              ...serviceMessage,
              type: "SERVICE",
            });
          }
        });
      }

      // Sort main messages by timestamp
      allMessages.sort((a, b) => {
        const timestampA = a.timestamp || 0;
        const timestampB = b.timestamp || 0;
        return timestampA - timestampB;
      });

      // Add regular messages
      if (initData.messages && Array.isArray(initData.messages)) {
        initData.messages.forEach((message) => {
          allMessages.push(message);
        });
      }

      // Group messages by queryId while preserving timestamp order
      const seenQueryIds = new Set();
      const blocks = [];

      allMessages.forEach((message) => {
        if (!seenQueryIds.has(message.queryId)) {
          // First time seeing this queryId, create a new block
          seenQueryIds.add(message.queryId);
          const isServiceMessage = message.type === "SERVICE";
          const isNotificationMessage = message.type === "NOTIFICATION";
          const isDialogMessage = message.type === "DIALOG";

          let blockMessage = message;
          if (isServiceMessage) {
            // Format service message to match automation block structure
            blockMessage = {
              format: message?.textMode?.toUpperCase(),
              content: message?.text,
            };
          } else if (isNotificationMessage || isDialogMessage) {
            blockMessage = message;
          }

          let blockFrom = "assistant"; // default
          if (isServiceMessage) blockFrom = "service";
          else if (isNotificationMessage || isDialogMessage)
            blockFrom = "notification";

          blocks.push({
            from: blockFrom,
            queryId: message.queryId,
            messages: [blockMessage],
          });
        } else {
          // Find existing block and add message to it
          const existingBlock = blocks.find(
            (block) => block.queryId === message.queryId
          );
          if (existingBlock) {
            existingBlock.messages.push(message);
          }
        }
      });

      // Add blocks to this session
      blocks.forEach((block) => {
        addNewBlock(block.queryId, block);
      });
    }
  }, [sessionData, addNewBlock]);

  // Expose functions to parent
  React.useImperativeHandle(ref, () => ({
    addNewBlock,
    addMessageToBlock,
    markBlockComplete,
    markBlockStopped,
    clearBlocks: () => {
      setMessageBlocks(new Map());
      setBlockOrder([]);
      setStoppedBlocks(new Set());
      blockRefs.current.clear();
    },
  }));

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div
      style={{
        flexGrow: 1,
        overflowY: "auto",
        paddingTop: "8px",
        paddingLeft: "16px",
        paddingRight: "16px",
        height: "100%",
        position: "relative",
        backgroundColor: "transparent",
      }}
      ref={messagesContainerRef}
    >
      {renderedBlocks}
      {errorMessage && <SystemMessage message={errorMessage} type="warning" />}
    </div>
  );
};

export default React.forwardRef(ChatSession);
